/**
 * CWR 1.3 — Happening Soon (tap-through; mockup dashboard-mockup.html:1845–1904).
 * Horizontal scroll of events + series; each card is one tap target → its detail
 * route. No per-card Book/View button. Renders nothing when empty.
 */
import { FocalImage } from '@/components/FocalImage';
import type { HappeningSoonItem } from '@/types/dashboard';
import { formatEventDate, spotsLabel } from '../homeFormat';
import { CalendarIcon, ChevronRightIcon, LocationIcon } from './icons';
import styles from '../BookingHubPage.module.css';

export interface HappeningSoonProps {
  items: HappeningSoonItem[];
  onOpenEvent: (id: string) => void;
  onOpenSeries: (seriesId: string) => void;
  onSeeAll: () => void;
}

function spotsVariant(spotsRemaining: number): 'sold' | 'low' | 'default' {
  if (spotsRemaining <= 0) return 'sold';
  if (spotsRemaining <= 3) return 'low';
  return 'default';
}

export function HappeningSoon({ items, onOpenEvent, onOpenSeries, onSeeAll }: HappeningSoonProps) {
  if (items.length === 0) return null;

  return (
    <section className={styles.happening} data-testid="home-happening">
      <div className={styles.happeningHeader}>
        <h2 className={styles.sectionTitle}>Happening Soon</h2>
        <button type="button" className={styles.sectionLink} onClick={onSeeAll}>
          See all
          <ChevronRightIcon />
        </button>
      </div>

      <div className={styles.happeningScroll}>
        {items.map((item) => {
          const key = item.kind === 'event' ? `event-${item.id}` : `series-${item.seriesId}`;
          const title = item.kind === 'event' ? item.title : item.serviceName;
          const date = item.kind === 'event' ? item.startTime : item.earliestStartsAt;
          const location = item.locationName || item.cityState;
          const variant = spotsVariant(item.spotsRemaining);
          const open = () => (item.kind === 'event' ? onOpenEvent(item.id) : onOpenSeries(item.seriesId));

          return (
            <button
              key={key}
              type="button"
              className={styles.happeningCard}
              onClick={open}
              aria-label={`View ${title}`}
            >
              {item.imageUrl ? (
                <FocalImage
                  src={item.imageUrl}
                  alt=""
                  className={styles.happeningImage}
                  focalPointX={item.focalPointX}
                  focalPointY={item.focalPointY}
                  focalPointScale={item.focalPointScale}
                  loading="lazy"
                >
                  <span className={styles.spotsPill} data-variant={variant}>
                    {spotsLabel(item.spotsRemaining)}
                  </span>
                </FocalImage>
              ) : (
                <div className={styles.happeningImage}>
                  <span className={styles.spotsPill} data-variant={variant}>
                    {spotsLabel(item.spotsRemaining)}
                  </span>
                </div>
              )}
              <div className={styles.happeningBody}>
                <h3 className={styles.happeningTitle}>{title}</h3>
                <div className={styles.happeningMetaRow}>
                  <CalendarIcon />
                  {formatEventDate(date)}
                </div>
                {location && (
                  <div className={styles.happeningMetaRow}>
                    <LocationIcon />
                    {location}
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}
